import { useMutation, gql } from '@apollo/client';
import { useScript } from '@do/seashell/hooks';
import React, {
  createContext,
  ReactElement,
  useCallback,
  useEffect,
  useState
} from 'react';
import copy from '../copy';
import {
  SendArkoseResult,
  SendArkoseResultVariables
} from '../generated-types/SendArkoseResult';

const SEND_ARKOSE_RESULT = gql`
  mutation SendArkoseResult($arkoseSessionToken: String!, $sessionId: String!) {
    SendArkoseResult(
      arkose_session_token: $arkoseSessionToken
      session_id: $sessionId
    ) {
      solved
    }
  }
`;

export enum ArkoseState {
  LOADING,
  LOADED,
  LOADING_FAILED,
  PROCESSING,
  PASSED,
  FAILED,
  ERROR
}

interface ArkoseError {
  title?: string;
  details: string;
}

export interface Arkose {
  run: (sessionId: string) => Promise<void>;
  reset: () => void;
  state: ArkoseState;
  error?: ArkoseError;
}

/**
 * ArkoseContext manages the context for the arkose security integration.
 * This includes the following:
 * 1. Loading the arkose js library when the app loads
 * 2. Exposing a run function to call when you are ready to run the arkose integration
 * 3. Exposing a reset function to reset the arkose state and remove error messages
 * 4. Exposing the current arkose state
 * 5. Exposing any arkose error messages
 *
 * Documentation: https://support.arkoselabs.com/hc/en-us/articles/4403746537875-Single-Page-Web-Application-Guide
 */
const ArkoseContext = createContext<Arkose>({
  run: (_sessionId: string) => new Promise<void>((resolve) => resolve()),
  reset: () => undefined,
  state: ArkoseState.LOADING
});

export function ArkoseContextProvider({
  children
}: {
  children: React.ReactNode;
}): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [arkose, setArkose] = useState<any>();
  const [isArkoseReady, setIsArkoseReady] = useState(false);
  const [state, setState] = useState<ArkoseState>(ArkoseState.LOADING);
  const [sendArkoseToken] = useMutation<
    SendArkoseResult,
    SendArkoseResultVariables
  >(SEND_ARKOSE_RESULT);
  const [errorMessage, setErrorMessage] = useState<ArkoseError | undefined>();

  // if the setup enforcement function is not already create, create it.
  if (!window.setupEnforcement) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setupEnforcement = (enforcement: any): void => {
      setArkose(enforcement);
    };

    window.setupEnforcement = setupEnforcement;
  }

  const [isLoaded, hasError] = useScript(
    `https://client-api.arkoselabs.com/v2/${process.env.REACT_APP_ARKOSE_KEY}/api.js`,
    {
      attributes: {
        defer: 'true',
        'data-callback': 'setupEnforcement',
        id: 'arkosescript'
      }
    }
  );

  // handles settings the state from the result of loading the arkose script
  useEffect(() => {
    if (isLoaded && !!arkose && state === ArkoseState.LOADING) {
      setState(ArkoseState.LOADED);
    } else if (hasError && state !== ArkoseState.LOADING_FAILED) {
      setState(ArkoseState.LOADING_FAILED);
      setErrorMessage(copy.errors.arkose_loading_failed);
    }
  }, [isLoaded, hasError, arkose, state]);

  const run = useCallback(
    (sessionId: string) => {
      return new Promise<void>((resolve, reject) => {
        if (state !== ArkoseState.LOADING && state !== ArkoseState.PROCESSING) {
          setErrorMessage(undefined);
          setState(ArkoseState.PROCESSING);
          arkose?.setConfig({
            async onCompleted(t: { token: string }) {
              try {
                const { data } = await sendArkoseToken({
                  variables: {
                    sessionId,
                    arkoseSessionToken: t.token
                  }
                });

                setState(
                  data?.SendArkoseResult?.solved
                    ? ArkoseState.PASSED
                    : ArkoseState.FAILED
                );

                if (data?.SendArkoseResult?.solved) {
                  resolve();
                } else {
                  setErrorMessage(copy.errors.arkose_failed);
                  reject();
                }
              } catch (e) {
                setState(ArkoseState.ERROR);
                setErrorMessage(copy.errors.unknown);
                reject();
              }
            },
            onReady() {
              setIsArkoseReady(true);
              arkose.run();
            }
          });

          if (isArkoseReady) {
            arkose.run();
          }
        } else {
          reject();
        }
      });
    },
    [arkose, isArkoseReady, state, sendArkoseToken]
  );

  const reset = useCallback(() => {
    switch (state) {
      case ArkoseState.PROCESSING:
      case ArkoseState.ERROR:
      case ArkoseState.PASSED:
      case ArkoseState.FAILED:
        setErrorMessage(undefined);
        setState(ArkoseState.LOADED);
        break;
      default:
        break;
    }
  }, [state, setState]);

  const arkoseContext: Arkose = { reset, run, state, error: errorMessage };

  return (
    <ArkoseContext.Provider value={arkoseContext}>
      {children}
    </ArkoseContext.Provider>
  );
}

export default ArkoseContext;
